import { supabase } from './supabase';

export type DuplicateReceiptMatch = {
  expenseId: string;
  description: string;
  expenseDate: string;
  amount: number;
  merchantName: string | null;
  approvalStatus?: string | null;
};

export class DuplicateReceiptError extends Error {
  readonly match: DuplicateReceiptMatch;

  constructor(match: DuplicateReceiptMatch) {
    super(
      `This receipt was already uploaded as "${match.description}" (${match.expenseDate}).`
    );
    this.name = 'DuplicateReceiptError';
    this.match = match;
  }
}

/** SHA-256 hex digest of file bytes. */
export async function computeFileSha256(file: File | Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function findDuplicateReceipt(
  businessId: string,
  fileHash: string
): Promise<DuplicateReceiptMatch | null> {
  const { data, error } = await supabase
    .from('expense_receipts')
    .select(
      `
      expense_id,
      expense:expenses (
        id,
        description,
        expense_date,
        amount,
        merchant_name,
        approval_status
      )
    `
    )
    .eq('business_id', businessId)
    .eq('file_hash', fileHash)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error('Duplicate receipt check failed:', error);
    return null;
  }

  const expense = data?.expense;
  if (!expense || Array.isArray(expense)) {
    if (Array.isArray(expense) && expense[0]) {
      return mapExpenseToMatch(expense[0] as Record<string, unknown>);
    }
    return null;
  }

  return mapExpenseToMatch(expense as Record<string, unknown>);
}

function mapExpenseToMatch(row: Record<string, unknown>): DuplicateReceiptMatch {
  return {
    expenseId: String(row.id),
    description: String(row.description ?? 'Expense'),
    expenseDate: String(row.expense_date ?? ''),
    amount: Number(row.amount ?? 0),
    merchantName: row.merchant_name ? String(row.merchant_name) : null,
    approvalStatus: row.approval_status ? String(row.approval_status) : null,
  };
}

export async function assertReceiptNotDuplicate(
  businessId: string,
  file: File | Blob
): Promise<string> {
  const fileHash = await computeFileSha256(file);
  const match = await findDuplicateReceipt(businessId, fileHash);
  if (match) {
    throw new DuplicateReceiptError(match);
  }
  return fileHash;
}

export function formatDuplicateReceiptMessage(match: DuplicateReceiptMatch): string {
  const date = match.expenseDate
    ? new Date(match.expenseDate + 'T12:00:00').toLocaleDateString()
    : '';
  const parts = [match.description];
  if (match.merchantName) parts.push(match.merchantName);
  if (date) parts.push(date);
  return `This receipt was already uploaded (${parts.join(' · ')}).`;
}
